import type { AxiosCollection, AxiosErrors } from '@/types/Components/Response'
import type {
  DeliveryMethodMappingEntry,
  Integration,
  IntegrationActionRequest,
  OngoingStatuses,
} from '@/types/Models/Integration'
import type { IntegrationsStoreState } from '@/types/Store'
import { defineStore } from 'pinia'
import { catchErrors } from '@/services/Helpers'
import CompanyProxy from '@/proxies/CompanyProxy'
import IntegrationsProxy from '@/proxies/Customer/IntegrationsProxy'
import IntegrationsTransformer from '@/transformers/IntegrationsTransformer'
import IntegrationOrderTransformer from '@/transformers/IntegrationOrderTransformer'

export const useCustomersIntegrationsStore = defineStore('customers/integrations', {
  state: (): IntegrationsStoreState => ({
    integrations: [],
    ordersCount: 0,
    ordersList: [],
    ongoingStatuses: [],
  }),
  actions: {
    resetList() {
      this.integrations = []
    },
    resetOrdersList() {
      this.ordersCount = 0
      this.ordersList = []
    },
    updateSettings({ customerId, integrationId, params }: { customerId: string; integrationId: string; params: any }) {
      return new IntegrationsProxy(customerId).update(integrationId, params).catch(catchErrors)
    },
    fetch(customerId: string) {
      return new IntegrationsProxy(customerId)
        .all()
        .then((response: AxiosCollection<Integration>) => {
          this.integrations = IntegrationsTransformer.fetchCollection(response.data)
          return response
        })
        .catch((error: AxiosErrors) => {
          catchErrors(error)
          return undefined
        })
    },
    destroy({ customerId, integrationId }: { customerId: string; integrationId: string }) {
      return new IntegrationsProxy(customerId).destroy(integrationId).catch(catchErrors)
    },
    oauthCallback(data: any) {
      return new IntegrationsProxy('').oauth(data)
    },
    connectShopify(params: { customerId: string; externalId: string; updateLabel: boolean }) {
      return new CompanyProxy().update(params.customerId, { externalId: params.externalId })
    },
    connect({ customerId, data }: { customerId: string; data: any }) {
      return new IntegrationsProxy(customerId).connect(data).catch(catchErrors)
    },
    runActions({ customerId, id, action, params }: IntegrationActionRequest) {
      return new IntegrationsProxy(customerId).runActions(id, action || '', params).catch(catchErrors)
    },
    forceImport({ customerId, id }: IntegrationActionRequest) {
      return new IntegrationsProxy(customerId).forceImport(id).catch(catchErrors)
    },
    syncSettings({ customerId, id }: IntegrationActionRequest) {
      return new IntegrationsProxy(customerId).syncSettings(id).catch(catchErrors)
    },
    orderMapping({ customerId, id, params }: IntegrationActionRequest) {
      return new IntegrationsProxy(customerId)
        .orderMapping(id, params)
        .then((response) => {
          if (response) {
            this.ordersCount = response.meta?.pagination.total || 0
            this.ordersList = IntegrationOrderTransformer.fetchCollection(response.data)
          }
          return response
        })
        .catch((error: AxiosErrors) => {
          catchErrors(error)
          return undefined
        })
    },
    fetchStatuses({ customerId, id }: { customerId: string; id: string }) {
      return this.runActions({ customerId, id, action: 'statuses', params: {} }).then(
        (response: AxiosCollection<OngoingStatuses>) => {
          if (response?.data) {
            this.ongoingStatuses = response.data
          }
          return response
        },
      )
    },
    resetOrders() {
      this.resetOrdersList()
    },
    reset() {
      this.resetList()
    },
    addDeliveryMethodMapping(
      mapping: Record<string, DeliveryMethodMappingEntry>,
    ): Record<string, DeliveryMethodMappingEntry> {
      const key = `Method ${Object.keys(mapping).length + 1}`
      return { ...mapping, [key]: { distributionChannel: 'package', serviceLevel: 'priority' } }
    },
    removeDeliveryMethodMapping(
      mapping: Record<string, DeliveryMethodMappingEntry>,
      methodName: string,
    ): Record<string, DeliveryMethodMappingEntry> {
      const result = { ...mapping }
      delete result[methodName]
      return result
    },
    updateDeliveryMethodMapping(
      mapping: Record<string, DeliveryMethodMappingEntry>,
      oldName: string,
      field: string,
      value: string,
    ): Record<string, DeliveryMethodMappingEntry> {
      const result = { ...mapping }
      if (field === 'methodName') {
        if (oldName === value) return result
        const entry = result[oldName]
        delete result[oldName]
        result[value] = entry
      } else {
        result[oldName] = { ...result[oldName], [field]: value }
      }
      return result
    },
  },
})

export type CustomersIntegrationsStore = ReturnType<typeof useCustomersIntegrationsStore>
